import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Action } from 'history'
import history from 'history/browser'
import { ROUTES } from '@constants/index'
import {
  drawerHeaderVisibleSelector,
  appVisibleSelector,
  ProgressStep,
  progressStepSelector,
  resetWorkflow,
  showApp,
  appClosingSelector,
  appVisibleAllowedSelector,
  setAppVisibleAllowed,
  appBeforeCloseCallbackSelector,
  hideNavigateBackSelector,
  navigateBackCallbackSelector,
  drawerHideCloseSelector,
} from '@redux/slices/workflowSlice'
import { sessionExpiredSelector } from '@redux/slices/connectorSlice'
import { insuranceOpenEvent, resetAnalyticsInitialState } from '@redux/slices/analyticsSlice'
import { resetCta } from '@redux/slices/ctaSlice'
import { resetForms } from '@redux/slices/formsSlice'
import { resetSubmitStatus } from '@redux/slices/claimSubmitSlice'
import { resetClaimStatus } from '@redux/slices/claimStatusSlice'
import { resetPrescriptionFileInitialState } from '@redux/slices/prescriptionFileSlice'
import { Logger } from '@utils/logger'
import {
  clearAllowDirectNavigation,
  clearLocalBasePath,
  isAllowDirectNavigation,
  isLocationAllowedToProceed,
  setLoginRedirectParam,
} from '@utils/navigationUtil'
import { scrollToDrawerTop } from '@utils/scrollToDrawerTop'
import { useEnvironment } from '@hooks/useEnvironment'
import { useInvokeAction } from '@hooks/useInvokeAction'
import { useWebchat } from '@hooks/useWebchat'
import { useRestrictedCheck } from './useRestrictedCheck'
import { RestrictedCheckStatus } from '@typesApp/Session'

type UseAppDrawerResult = {
  isVisible: boolean
  showHeader: boolean
  activeStep: ProgressStep
  onClose: () => void
  onNavigateBack?: () => void
  hideNavigateBack?: boolean
  hideCloseButton?: boolean
}

export const useAppDrawer = (): UseAppDrawerResult => {
  const dispatch = useDispatch()
  const { brand, basePath, basePathSearch, signInPath } = useEnvironment()
  const location = useLocation()
  const activeStep = useSelector(progressStepSelector)
  const isVisible = useSelector(appVisibleSelector)
  const appClosing = useSelector(appClosingSelector)
  const onBeforeClose = useSelector(appBeforeCloseCallbackSelector)
  const appVisibleAllowed = useSelector(appVisibleAllowedSelector)
  const showHeader = useSelector(drawerHeaderVisibleSelector)
  const hideCloseButton = useSelector(drawerHideCloseSelector)
  const sessionExpired = useSelector(sessionExpiredSelector)
  const hideNavigateBack = useSelector(hideNavigateBackSelector)
  const onNavigateBack = useSelector(navigateBackCallbackSelector)
  const checkRestricted = useRestrictedCheck()
  const notifyFormAction = useInvokeAction()
  const { hideWebchat, resetWebchat } = useWebchat()

  const openDrawer = () => {
    dispatch(showApp())
    dispatch(insuranceOpenEvent())
    hideWebchat()
  }

  const handleNonAllowedMatch = () => {
    if (appVisibleAllowed) return

    if (isAllowDirectNavigation()) {
      clearAllowDirectNavigation()
      dispatch(setAppVisibleAllowed())
      return
    }

    const match = window.location.pathname.includes(ROUTES.BASE)

    // reloaded when drawer was open
    if (match && isLocationAllowedToProceed()) {
      dispatch(setAppVisibleAllowed())
      return
    }

    // reject direct url access or last path since reload was different
    if (match) {
      history.replace(`${basePath}${basePathSearch}`)
    }

    clearLocalBasePath()
  }

  useEffect(() => {
    const match = location.pathname.includes(ROUTES.BASE)
    if (match && !isVisible && !appClosing) {
      // allowed navigation or page reload while drawer open
      if (appVisibleAllowed || isVisible) {
        const loginRedirect = window.location.pathname.includes(ROUTES.LOGIN_REDIRECT)
        if (loginRedirect) {
          handleLoginRedirect()
        } else {
          openDrawer()
        }
      } else {
        handleNonAllowedMatch()
      }
    }
  }, [brand, location, appVisibleAllowed, isVisible, appClosing])

  /**
   * Suppress open drawer if restricted status
   */
  const handleLoginRedirect = () => {
    const callback = (status: RestrictedCheckStatus) => {
      if (!status.restricted) {
        openDrawer()
      } else {
        // restore url to basePath
        if (location.pathname.includes(ROUTES.BASE)) {
          history.replace(`${basePath}${basePathSearch}`)
        }
      }
    }

    checkRestricted({ onSuccess: callback })
  }

  /**
   * On back navigation, clean up state if drawer is not closed
   */
  useEffect(() => {
    if (!isVisible) return

    const unlisten = history.listen(({ location, action }) => {
      // cleanup listener first as using hook cleanup had a side
      // effect of removing the listener before this code can execute
      unlisten()

      if (action === Action.Pop && isVisible) {
        // back navigation when drawer still open, need to reset state
        const pathMatch = location.pathname.includes(`${ROUTES.BASE}`)
        if (!pathMatch) {
          resetState()
        }
      }
    })
  }, [isVisible])

  useEffect(() => {
    if (isVisible && appClosing) {
      cleanupOnClose()
    }
  }, [appClosing])

  /**
   * Step navigation
   */
  useEffect(() => {
    if (isVisible && !appClosing) {
      scrollToDrawerTop()
      notifyFormAction()
      hideWebchat()
    }
  }, [activeStep])

  useEffect(() => {
    if (sessionExpired) {
      resetState()

      // to allow commerce app to redirect back on next sign in
      if (isVisible) {
        setLoginRedirectParam({ basePath, signInPath })
      }
    }
  }, [sessionExpired])

  const resetState = () => {
    resetWebchat()
    dispatch(resetCta())
    dispatch(resetForms())
    dispatch(resetWorkflow())
    dispatch(resetClaimStatus())
    dispatch(resetSubmitStatus())
    dispatch(resetPrescriptionFileInitialState())
    dispatch(resetAnalyticsInitialState())
  }

  const cleanupOnClose = () => {
    // replace our history with base path (e.g. cart or checkout)
    // so that back button will work correctly
    history.replace(`${basePath}${basePathSearch}`)
    resetState()
    clearLocalBasePath()
  }

  /**
   * Handle app drawer close button - invokes onBeforeClose to perform optional, view specific
   * cleanup.
   */
  const onClose = () => {
    if (!onBeforeClose || typeof onBeforeClose !== 'function') {
      cleanupOnClose()
      return
    }

    onBeforeClose()
      .catch(error => Logger.warn('Error on app drawer before close', error))
      .finally(cleanupOnClose)
  }

  return {
    isVisible,
    showHeader,
    activeStep,
    onClose,
    onNavigateBack,
    hideNavigateBack,
    hideCloseButton,
  }
}
